/**
 * Overview Panel — edit a flag's core fields + drive its lifecycle.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { useI18n } from '@/i18n';
import { parseBackendDateTime } from '@/lib/format/datetime';
import type { FlagResponse, FlagType, LifecycleState } from '@/types/featureFlags';
import { useUpdateFlag, useTransitionFlag, useDeleteFlag } from '../hooks/useFeatureFlags';

interface OverviewPanelProps {
  flag: FlagResponse;
  onDeleted?: () => void;
}

const FLAG_TYPES: FlagType[] = ['deployment', 'release', 'isolation', 'ops'];

/** YYYY-MM-DD for <input type="date"> from an ISO string. */
function toDateInput(iso?: string): string {
  if (!iso) return '';
  const d = parseBackendDateTime(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function OverviewPanel({ flag, onDeleted }: OverviewPanelProps) {
  const { t } = useI18n();
  const updateMutation = useUpdateFlag();
  const transitionMutation = useTransitionFlag();
  const deleteMutation = useDeleteFlag();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [flagType, setFlagType] = useState<FlagType>(flag.flag_type);
  const [defaultValue, setDefaultValue] = useState<boolean>(flag.default_value === true);
  const [rollout, setRollout] = useState(flag.rollout_percentage);
  const [expiresAt, setExpiresAt] = useState(toDateInput(flag.expires_at));
  const [displayName, setDisplayName] = useState(flag.display_name ?? '');
  const [owner, setOwner] = useState(flag.owner ?? '');
  const [description, setDescription] = useState(flag.description ?? '');

  useEffect(() => {
    setFlagType(flag.flag_type);
    setDefaultValue(flag.default_value === true);
    setRollout(flag.rollout_percentage);
    setExpiresAt(toDateInput(flag.expires_at));
    setDisplayName(flag.display_name ?? '');
    setOwner(flag.owner ?? '');
    setDescription(flag.description ?? '');
  }, [flag]);

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        flagKey: flag.flag_key,
        data: {
          flag_type: flagType,
          default_value: defaultValue,
          rollout_percentage: rollout,
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
          display_name: displayName.trim() || undefined,
          owner: owner.trim() || undefined,
          description: description.trim() || undefined,
        },
      });
      toast.success(t('featureFlags.overview.saved'));
    } catch {
      // toast already shown by mutation onError
    }
  };

  const handleTransition = async (newState: LifecycleState) => {
    try {
      await transitionMutation.mutateAsync({ flagKey: flag.flag_key, data: { new_state: newState } });
    } catch {
      // toast already shown by mutation onError
    }
  };

  const handleDelete = async () => {
    try {
      // On success the flags query invalidates; FeatureFlagsPage re-selects another flag.
      await deleteMutation.mutateAsync(flag.flag_key);
      setShowDeleteConfirm(false);
      onDeleted?.();
    } catch {
      // toast already shown by mutation onError
    }
  };

  const isBooleanFlag = flag.value_type === 'boolean';
  // Rollout only ENABLES on top of the default (evaluator.py never disables via
  // rollout). When the flag defaults to ON, every user is already on — so the
  // rollout slider is a no-op. Disable it and say so, instead of showing the
  // contradictory "only N% of users will see this enabled".
  const rolloutNoOp = isBooleanFlag && defaultValue;

  return (
    <div className="space-y-6">
      {/* Hero — runtime state: what the flag actually does for users right now */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-5">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{t('featureFlags.overview.sectionRuntime')}</h3>
          <p className="text-xs text-gray-500">{t('featureFlags.overview.sectionRuntimeHint')}</p>
        </div>

        {/* Default value (boolean flags) */}
        {isBooleanFlag && (
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              {t('featureFlags.overview.enabledByDefault')}
            </label>
            <Switch
              checked={defaultValue}
              onChange={setDefaultValue}
              ariaLabel={t('featureFlags.overview.enabledByDefault')}
            />
          </div>
        )}

        {/* Rollout */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('featureFlags.overview.rolloutPercentage')}
          </label>
          <div className={`flex items-center gap-3 ${rolloutNoOp ? 'opacity-50' : ''}`}>
            <input
              type="range"
              min={0}
              max={100}
              value={rollout}
              disabled={rolloutNoOp}
              onChange={(e) => setRollout(Number(e.target.value))}
              className="flex-1 disabled:cursor-not-allowed"
            />
            <div className="w-16">
              <input
                type="number"
                min={0}
                max={100}
                value={rollout}
                disabled={rolloutNoOp}
                onChange={(e) => setRollout(Math.min(100, Math.max(0, Number(e.target.value))))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
              />
            </div>
            <span className="text-sm text-gray-500">%</span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {rolloutNoOp
              ? t('featureFlags.overview.rolloutIgnoredWhenDefaultOn')
              : rollout < 100
                ? t('featureFlags.overview.rolloutPartialUsers', { percentage: String(rollout) })
                : t('featureFlags.overview.rolloutAllUsers')}
          </p>
        </div>
      </div>

      {/* Basics — secondary metadata, de-emphasized */}
      <div className="space-y-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          {t('featureFlags.overview.sectionBasics')}
        </h3>

        <Input
          label={t('featureFlags.overview.displayName')}
          value={displayName}
          maxLength={100}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={t('featureFlags.overview.displayNamePlaceholder')}
        />

        <Select
          label={t('featureFlags.overview.flagType')}
          value={flagType}
          onChange={(e) => setFlagType(e.target.value as FlagType)}
          options={FLAG_TYPES.map((ft) => ({ value: ft, label: ft }))}
        />

        <Input
          label={t('featureFlags.overview.owner')}
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          placeholder={t('featureFlags.overview.ownerPlaceholder')}
        />

        <Input
          type="date"
          label={t('featureFlags.overview.expiresAt')}
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
        />

        <Textarea
          label={t('featureFlags.overview.description')}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('featureFlags.overview.descriptionPlaceholder')}
        />
      </div>

      <div className="flex justify-end pt-2 border-t border-gray-200">
        <Button variant="primary" onClick={handleSave} loading={updateMutation.isPending}>
          {t('featureFlags.overview.save')}
        </Button>
      </div>

      {/* Lifecycle — state transitions (less central than runtime state, kept lower) */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div>
          <h3 className="text-sm font-medium text-gray-900">{t('featureFlags.overview.lifecycle')}</h3>
          <p className="text-xs text-gray-500">{t('featureFlags.overview.lifecycleHint')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {flag.lifecycle_state === 'draft' && (
            <Button variant="primary" size="sm" onClick={() => handleTransition('active')} disabled={transitionMutation.isPending}>
              {t('featureFlags.overview.activate')}
            </Button>
          )}
          {flag.lifecycle_state === 'active' && (
            <>
              <Button variant="secondary" size="sm" onClick={() => handleTransition('draft')} disabled={transitionMutation.isPending}>
                {t('featureFlags.overview.backToDraft')}
              </Button>
              <Button variant="danger" size="sm" onClick={() => handleTransition('archived')} disabled={transitionMutation.isPending}>
                {t('featureFlags.overview.archive')}
              </Button>
            </>
          )}
          {flag.lifecycle_state === 'archived' && (
            <Button variant="secondary" size="sm" onClick={() => handleTransition('active')} disabled={transitionMutation.isPending}>
              {t('featureFlags.overview.restore')}
            </Button>
          )}
        </div>
      </div>

      {/* Danger zone — permanent hard delete (distinct from archive) */}
      <div className="border border-red-200 rounded-lg p-6 space-y-3">
        <div>
          <h3 className="text-sm font-medium text-red-700">{t('featureFlags.overview.dangerZone')}</h3>
          <p className="text-xs text-gray-500">{t('featureFlags.overview.deleteHint')}</p>
        </div>
        <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>
          {t('featureFlags.overview.delete')}
        </Button>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('featureFlags.deleteConfirm.title')}</h3>
            <p className="text-sm text-gray-600">
              {t('featureFlags.deleteConfirm.message', { name: flag.flag_key })}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={deleteMutation.isPending}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={handleDelete} loading={deleteMutation.isPending}>
                {t('featureFlags.deleteConfirm.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
